/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, PageGridContainer, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/consumer-home';

type PropsToModule = {
  loading?: boolean;
  message?: React.ReactElement;
  onEditButtonClick?: () => void;
  hasCompletedOnboarding?: boolean;
  expandedSection?: React.ReactElement | null;
  panelOpen?: boolean;
  isEnterprise?: boolean;
};

const styles = {
  wrapperContainer: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-800) var(--cds-spacing-200) var(--cds-spacing-200);
    min-height: 64px;
    ${breakpoints.down('sm')} {
      padding-right: var(--cds-spacing-400);
    }
  `,
  resultsContainer: css`
    margin: 0 auto;
    display: flex;
    justify-content: flex-end;
    max-width: 1375px;
    gap: 25px;
    ${breakpoints.down('sm')} {
      justify-content: flex-start;
    }
  `,
  onboardingResults: css`
    && {
      overflow: hidden;
      line-height: 32px;

      strong {
        color: var(--cds-color-neutral-primary);
        font-family: inherit;
      }
    }
  `,
  cta: css`
    && {
      ${typography2.actionPrimary}
      padding: 0;

      :hover {
        background: none;
        box-shadow: none;
        border: none;
        text-decoration: underline;
      }
    }
  `,
  enterpriseHomeRefreshContainer: css`
    border-radius: 16px;
    border: 1px solid var(--cds-color-grey-50);
    padding: var(--cds-spacing-200);
  `,
  editButton: css`
    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
};

const OnboardingResultsHeaderModule = ({
  loading,
  message,
  onEditButtonClick,
  hasCompletedOnboarding,
  expandedSection,
  panelOpen,
  isEnterprise,
}: PropsToModule) => {
  if (loading) {
    return null;
  }

  if (isEnterprise) {
    return (
      <HomeSection className="PageConfig_OnboardingResultsHeaderComponent" onlyTopPadding={true}>
        <PageGridContainer>
          <Grid item xs={12}>
            <Grid item container xs={12} alignItems="center" css={styles.enterpriseHomeRefreshContainer}>
              <Typography2 component="p" variant="bodyPrimary">
                {message}
              </Typography2>
              <Button
                css={styles.editButton}
                variant="ghost"
                textAlign="start"
                size="small"
                onClick={onEditButtonClick}
                aria-label={_t('Set your learning goal and role.')}
                data-testid="front-desk-one-liner-cta"
              >
                {hasCompletedOnboarding ? _t('Edit') : _t('Set your goals today')}
              </Button>
            </Grid>
          </Grid>
        </PageGridContainer>
      </HomeSection>
    );
  }

  return (
    <div css={styles.wrapperContainer}>
      {!loading && (
        <React.Fragment>
          <div css={styles.resultsContainer}>
            <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.onboardingResults}>
              {message}
            </Typography2>
            <Button
              variant="ghost"
              edgeAlign="start"
              size="small"
              onClick={onEditButtonClick}
              css={styles.cta}
              component={TrackedButton}
              requireFullyVisible={false}
              withVisibilityTracking={false}
              trackingName="onboarding_results_header_edit"
              aria-label={_t('Set your learning goal and role.')}
              aria-expanded={panelOpen}
              aria-controls="front-desk-panel"
              id="front-desk-cta"
              data-testid="front-desk-one-liner-cta"
            >
              {hasCompletedOnboarding ? _t('Edit') : _t('Set your goals today')}
            </Button>
          </div>
          {expandedSection}
        </React.Fragment>
      )}
    </div>
  );
};

export default OnboardingResultsHeaderModule;
